import React, { useState } from 'react';
import { 
  Calendar, 
  CheckCircle, 
  XCircle, 
  Plus,
  Download,
  X
} from 'lucide-react';
import Layout from '../components/Layout';
import { useApp } from '../contexts/AppContext';
import { AttendanceStatus, Student, BehaviorIncident } from '../types';
import { format, addDays, subDays } from 'date-fns';
import toast from 'react-hot-toast';

const AttendancePage: React.FC = () => {
  const {
    students,
    classes,
    attendanceRecords,
    behaviorIncidents,
    teacherAttendance,
    selectedClass,
    selectedDate,
    setSelectedClass,
    setSelectedDate,
    markAttendance,
    bulkMarkAttendance,
    addBehaviorIncident,
    getStudentsInClass,
    updateBehaviorIncident,
    deleteBehaviorIncident,
    markTeacherAttendance,
    loading
  } = useApp();

  const [bulkAction, setBulkAction] = useState<AttendanceStatus | null>(null);
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [notes, setNotes] = useState<{ [key: string]: string }>({});
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showBehaviorModal, setShowBehaviorModal] = useState(false);
  const [showIncidentDetailsModal, setShowIncidentDetailsModal] = useState(false);
  const [selectedStudentForBehavior, setSelectedStudentForBehavior] = useState<Student | null>(null);
  const [selectedIncident, setSelectedIncident] = useState<BehaviorIncident | null>(null);
  const [newBehaviorIncident, setNewBehaviorIncident] = useState({
    type: 'other' as const,
    description: '',
    severity: 'low' as const,
    resolved: false,
    classId: '',
    createdAt: new Date(),
    updatedAt: new Date()
  });

  // Get students for the selected class
  const [classStudents, setClassStudents] = useState<Student[]>([]);
  
  React.useEffect(() => {
    const loadClassStudents = async () => {
      if (selectedClass) {
        try {
          const studentsInClass = await getStudentsInClass(selectedClass);
          setClassStudents(studentsInClass);
        } catch (error) {
          console.error('Error loading class students:', error);
          toast.error('Failed to load students for this class');
        }
      } else {
        setClassStudents([]);
      }
    };
    
    loadClassStudents();
  }, [selectedClass, getStudentsInClass]);
  const todayRecords = attendanceRecords.filter(record => record.date === selectedDate);

  const getAttendanceStatus = (studentId: string): AttendanceStatus => {
    const record = todayRecords.find(r => r.studentId === studentId);
    const status = record?.status || 'unmarked';
    console.log(`Student ${studentId} status:`, status, 'record:', record);
    return status;
  };

  const getAttendanceNotes = (studentId: string): string => {
    const record = todayRecords.find(r => r.studentId === studentId);
    return record?.notes || '';
  };

  const handleStatusChange = async (studentId: string, status: AttendanceStatus) => {
    try {
      await markAttendance(studentId, status, notes[studentId] || '');
      toast.success('Attendance updated');
    } catch (error) {
      toast.error('Failed to update attendance');
    }
  };

  const handleNotesChange = (studentId: string, note: string) => {
    setNotes(prev => ({ ...prev, [studentId]: note }));
  };

  const handleBulkAction = async () => {
    if (!bulkAction || selectedStudents.length === 0) return;

    try {
      await bulkMarkAttendance(selectedStudents, bulkAction);
      setSelectedStudents([]);
      setShowBulkModal(false);
      toast.success(`Marked ${selectedStudents.length} students as ${bulkAction}`);
    } catch (error) {
      toast.error('Failed to update attendance');
    }
  };

  const toggleStudentSelection = (studentId: string) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const getStudentBehaviorIncidents = (studentId: string): BehaviorIncident[] => {
    return behaviorIncidents.filter(incident => {
      if (!incident.timestamp) return false;
      const timestamp = incident.timestamp instanceof Date ? incident.timestamp : new Date(incident.timestamp);
      return incident.studentId === studentId && 
             timestamp.toISOString().split('T')[0] === selectedDate;
    });
  };

  const handleAddBehaviorIncident = async () => {
    if (!selectedStudentForBehavior || !newBehaviorIncident.description.trim() || !selectedClass) return;

    try {
      await addBehaviorIncident({
        ...newBehaviorIncident,
        studentId: selectedStudentForBehavior.id,
        classId: selectedClass,
        timestamp: new Date(),
        createdAt: new Date(),
        updatedAt: new Date()
      });
      
      setNewBehaviorIncident({
        type: 'other',
        description: '',
        severity: 'low',
        resolved: false,
        classId: '',
        createdAt: new Date(),
        updatedAt: new Date()
      });
      setShowBehaviorModal(false);
      setSelectedStudentForBehavior(null);
      toast.success('Behavior incident added successfully');
    } catch (error) {
      toast.error('Failed to add behavior incident');
    }
  };

  const handleResolveBehaviorIncident = async (incidentId: string) => {
    try {
      await updateBehaviorIncident(incidentId, { resolved: true });
      toast.success('Behavior incident resolved');
    } catch (error) {
      toast.error('Failed to resolve behavior incident');
    }
  };

  const handleDeleteBehaviorIncident = async (incidentId: string) => {
    try {
      await deleteBehaviorIncident(incidentId);
      toast.success('Behavior incident deleted');
    } catch (error) {
      toast.error('Failed to delete behavior incident');
    }
  };

  const selectAllStudents = () => {
    setSelectedStudents(classStudents.map(s => s.id));
  };

  const clearSelection = () => {
    setSelectedStudents([]);
  };


  const getStatusColor = (status: AttendanceStatus) => {
    let colorClass = '';
    switch (status) {
      case 'present':
        colorClass = 'status-present';
        break;
      case 'absent':
        colorClass = 'status-absent';
        break;
      case 'late':
        colorClass = 'status-late';
        break;
      case 'excused':
        colorClass = 'status-excused';
        break;
      case 'unmarked':
        colorClass = 'bg-gray-200 text-gray-600 border-gray-400 border-dashed';
        break;
      default:
        colorClass = 'bg-gray-100 text-gray-800 border-gray-200';
    }
    console.log(`Status ${status} -> Color class: ${colorClass}`);
    return colorClass;
  };

  const getStatusStyle = (status: AttendanceStatus) => {
    switch (status) {
      case 'present':
        return { backgroundColor: '#10b981', color: 'white', borderColor: '#059669' };
      case 'absent':
        return { backgroundColor: '#ef4444', color: 'white', borderColor: '#dc2626' };
      case 'late':
        return { backgroundColor: '#eab308', color: 'white', borderColor: '#ca8a04' };
      case 'excused':
        return { backgroundColor: '#f97316', color: 'white', borderColor: '#ea580c' };
      case 'unmarked':
        return { backgroundColor: '#e5e7eb', color: '#4b5563', borderColor: '#9ca3af', borderStyle: 'dashed' };
      default:
        return { backgroundColor: '#f3f4f6', color: '#1f2937', borderColor: '#d1d5db' };
    }
  };

  const getTeacherAttendanceStatus = () => {
    if (!selectedClass) return null;
    return teacherAttendance.find(ta => ta.classId === selectedClass && ta.date === selectedDate);
  };

  const getIncidentLabelStyle = (incident: BehaviorIncident) => {
    const baseClasses = "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity";
    
    if (incident.resolved) {
      return `${baseClasses} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
    }
    
    switch (incident.severity) {
      case 'high':
        return `${baseClasses} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`;
      case 'medium':
        return `${baseClasses} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`;
      case 'low':
        return `${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200`;
    }
  };

  const handleIncidentClick = (incident: BehaviorIncident) => {
    setSelectedIncident(incident);
    setShowIncidentDetailsModal(true);
  };

  const getAttendanceStats = () => {
    const total = classStudents.length;
    const present = todayRecords.filter(r => r.status === 'present').length;
    const absent = todayRecords.filter(r => r.status === 'absent').length;
    const late = todayRecords.filter(r => r.status === 'late').length;
    const excused = todayRecords.filter(r => r.status === 'excused').length;
    const unmarked = total - (present + absent + late + excused);
    
    return { total, present, absent, late, excused, unmarked };
  };

  const stats = getAttendanceStats();

  if (loading) {
    return (
      <Layout>
        <div className="p-6">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="h-16 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Attendance</h1>
            <p className="mt-2 text-gray-600 dark:text-gray-300">Track and manage student attendance</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => setShowBulkModal(true)}
              disabled={selectedStudents.length === 0}
              className="btn-secondary flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="h-5 w-5 mr-2" />
              Bulk Actions
            </button>
            <button className="btn-secondary flex items-center">
              <Download className="h-5 w-5 mr-2" />
              Export
            </button>
          </div>
        </div>

        {/* Controls */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Class
            </label>
            <select
              value={selectedClass || ''}
              onChange={(e) => setSelectedClass(e.target.value || null)}
              className="input-field"
            >
              <option value="">Select a class</option>
              {classes.map(classItem => (
                <option key={classItem.id} value={classItem.id}>
                  {classItem.name}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Date
            </label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="input-field"
            />
          </div>
          
          <div className="flex items-end">
            <div className="flex space-x-2">
              <button
                onClick={() => {
                  const currentDate = new Date(selectedDate + 'T00:00:00');
                  const previousDate = subDays(currentDate, 1);
                  setSelectedDate(format(previousDate, 'yyyy-MM-dd'));
                }}
                className="btn-secondary"
              >
                Previous
              </button>
              <button
                onClick={() => setSelectedDate(format(new Date(), 'yyyy-MM-dd'))}
                className="btn-secondary"
              >
                Today
              </button>
              <button
                onClick={() => {
                  const currentDate = new Date(selectedDate + 'T00:00:00');
                  const nextDate = addDays(currentDate, 1);
                  setSelectedDate(format(nextDate, 'yyyy-MM-dd'));
                }}
                className="btn-secondary"
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {!selectedClass ? (
          <div className="text-center py-12">
            <Calendar className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No class selected</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Please select a class to view attendance.</p>
          </div>
        ) : (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <div className="card text-center">
                <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Total</div>
              </div>
              <div className="card text-center">
                <div className="text-2xl font-bold text-green-600">{stats.present}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Present</div>
              </div>
              <div className="card text-center">
                <div className="text-2xl font-bold text-red-600">{stats.absent}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Absent</div>
              </div>
              <div className="card text-center">
                <div className="text-2xl font-bold text-yellow-600">{stats.late}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Late</div>
              </div>
              <div className="card text-center">
                <div className="text-2xl font-bold text-orange-600">{stats.excused}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Excused</div>
              </div>
              <div className="card text-center">
                <div className="text-2xl font-bold text-gray-400">{stats.unmarked}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Unmarked</div>
              </div>
            </div>

            {/* Teacher Attendance */}
            {selectedClass && (
              <div className="card">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Teacher Attendance
                  </h3>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => markTeacherAttendance(selectedClass, 'present')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                        getTeacherAttendanceStatus()?.status === 'present'
                          ? 'bg-green-500 text-white border-green-600 dark:bg-green-600 dark:text-white dark:border-green-700'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700'
                      }`}
                    >
                      <CheckCircle className="h-4 w-4 mr-2 inline" />
                      Present
                    </button>
                    <button
                      onClick={() => markTeacherAttendance(selectedClass, 'absent')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                        getTeacherAttendanceStatus()?.status === 'absent'
                          ? 'bg-red-500 text-white border-red-600 dark:bg-red-600 dark:text-white dark:border-red-700'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700'
                      }`}
                    >
                      <XCircle className="h-4 w-4 mr-2 inline" />
                      Absent
                    </button>
                  </div>
                </div>
                {getTeacherAttendanceStatus()?.notes && (
                  <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      <strong>Notes:</strong> {getTeacherAttendanceStatus()?.notes}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Attendance List */}
            <div className="card">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Students ({classStudents.length})
                </h3>
                <div className="flex space-x-2">
                  <button
                    onClick={selectAllStudents}
                    className="text-sm text-primary-600 hover:text-primary-500"
                  >
                    Select All
                  </button>
                  <button
                    onClick={clearSelection}
                    className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    Clear
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {classStudents.map((student) => {
                  const currentStatus = getAttendanceStatus(student.id);
                  const currentNotes = getAttendanceNotes(student.id);
                  
                  return (
                    <div
                      key={student.id}
                      className={`p-4 rounded-lg border-2 ${
                        selectedStudents.includes(student.id)
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <input
                            type="checkbox"
                            checked={selectedStudents.includes(student.id)}
                            onChange={() => toggleStudentSelection(student.id)}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          />
                          
                          <div className="flex-1">
                            <h4 className="text-lg font-medium text-gray-900 dark:text-white">{student.firstName} {student.lastName}</h4>
                            <p className="text-sm text-gray-500">
                              {student.email || 'No email'}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          {/* Status Buttons */}
                          <div className="flex space-x-1">
                            {(['present', 'absent', 'late', 'excused'] as AttendanceStatus[]).map((status) => (
                              <button
                                key={status}
                                onClick={() => handleStatusChange(student.id, status)}
                                className={`px-3 py-1 rounded-full text-sm font-medium border transition-colors ${
                                  currentStatus === status
                                    ? getStatusColor(status)
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600'
                                }`}
                                style={currentStatus === status ? getStatusStyle(status) : {}}
                              >
                                {status.replace('-', ' ').toUpperCase()}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Notes */}
                      <div className="mt-3">
                        <textarea
                          value={notes[student.id] || currentNotes}
                          onChange={(e) => handleNotesChange(student.id, e.target.value)}
                          onBlur={() => handleStatusChange(student.id, currentStatus)}
                          placeholder="Add notes..."
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          rows={2}
                        />
                      </div>

                      {/* Behavior Incidents */}
                      <div className="mt-4">
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">Behavior Incidents</h5>
                          <button
                            onClick={() => {
                              setSelectedStudentForBehavior(student);
                              setShowBehaviorModal(true);
                            }}
                            className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 flex items-center"
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Add Incident
                          </button>
                        </div>
                        
                        {getStudentBehaviorIncidents(student.id).length === 0 ? (
                          <p className="text-xs text-gray-500 dark:text-gray-400">No incidents today</p>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {getStudentBehaviorIncidents(student.id).map((incident) => (
                              <span
                                key={incident.id}
                                onClick={() => handleIncidentClick(incident)}
                                className={getIncidentLabelStyle(incident)}
                                title={`${incident.type.replace('-', ' ').toUpperCase()} - ${incident.severity} - Click for details`}
                              >
                                {incident.type.replace('-', ' ').toUpperCase()}
                                {incident.resolved && ' ✓'}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {/* Bulk Actions Modal */}
        {showBulkModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Bulk Actions ({selectedStudents.length} students selected)
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mark as:
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {(['present', 'absent', 'late', 'excused'] as AttendanceStatus[]).map((status) => (
                        <button
                          key={status}
                          onClick={() => setBulkAction(status)}
                          className={`p-2 rounded-lg border text-sm font-medium ${
                            bulkAction === status
                              ? getStatusColor(status)
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600'
                          }`}
                          style={bulkAction === status ? getStatusStyle(status) : {}}
                        >
                          {status.replace('-', ' ').toUpperCase()}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      onClick={() => setShowBulkModal(false)}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleBulkAction}
                      disabled={!bulkAction}
                      className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Apply to {selectedStudents.length} Students
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Behavior Incident Modal */}
        {showBehaviorModal && selectedStudentForBehavior && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Add Behavior Incident - {selectedStudentForBehavior.firstName} {selectedStudentForBehavior.lastName}
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Incident Type
                    </label>
                    <select
                      value={newBehaviorIncident.type}
                      onChange={(e) => setNewBehaviorIncident(prev => ({ ...prev, type: e.target.value as any }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                      <option value="profane-language">Profane Language</option>
                      <option value="disruption">Disruption</option>
                      <option value="aggression">Aggression</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Severity
                    </label>
                    <select
                      value={newBehaviorIncident.severity}
                      onChange={(e) => setNewBehaviorIncident(prev => ({ ...prev, severity: e.target.value as any }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Description
                    </label>
                    <textarea
                      value={newBehaviorIncident.description}
                      onChange={(e) => setNewBehaviorIncident(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Describe the incident..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                      rows={3}
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="resolved"
                      checked={newBehaviorIncident.resolved}
                      onChange={(e) => setNewBehaviorIncident(prev => ({ ...prev, resolved: e.target.checked }))}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label htmlFor="resolved" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      Mark as resolved
                    </label>
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => {
                      setShowBehaviorModal(false);
                      setSelectedStudentForBehavior(null);
                      setNewBehaviorIncident({
                        type: 'other',
                        description: '',
                        severity: 'low',
                        resolved: false,
                        classId: '',
                        createdAt: new Date(),
                        updatedAt: new Date()
                      });
                    }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddBehaviorIncident}
                    disabled={!newBehaviorIncident.description.trim()}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Add Incident
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Incident Details Modal */}
        {showIncidentDetailsModal && selectedIncident && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Incident Details
                  </h3>
                  <button
                    onClick={() => setShowIncidentDetailsModal(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Type
                    </label>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getIncidentLabelStyle(selectedIncident)}`}>
                      {selectedIncident.type.replace('-', ' ').toUpperCase()}
                    </span>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Severity
                    </label>
                    <span className={`inline-flex items-center px-2 py-1 rounded text-sm ${
                      selectedIncident.severity === 'high'
                        ? 'bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200'
                        : selectedIncident.severity === 'medium'
                        ? 'bg-yellow-200 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200'
                        : 'bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-gray-200'
                    }`}>
                      {selectedIncident.severity.toUpperCase()}
                    </span>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Description
                    </label>
                    <p className="text-sm text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-700 p-3 rounded-md">
                      {selectedIncident.description}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Status
                    </label>
                    <span className={`inline-flex items-center px-2 py-1 rounded text-sm ${
                      selectedIncident.resolved
                        ? 'bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-200'
                        : 'bg-yellow-200 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200'
                    }`}>
                      {selectedIncident.resolved ? 'RESOLVED' : 'PENDING'}
                    </span>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Time
                    </label>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {format(selectedIncident.timestamp, 'MMM d, yyyy h:mm a')}
                    </p>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  {!selectedIncident.resolved && (
                    <button
                      onClick={() => {
                        handleResolveBehaviorIncident(selectedIncident.id);
                        setShowIncidentDetailsModal(false);
                      }}
                      className="btn-secondary text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
                    >
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Mark Resolved
                    </button>
                  )}
                  <button
                    onClick={() => {
                      handleDeleteBehaviorIncident(selectedIncident.id);
                      setShowIncidentDetailsModal(false);
                    }}
                    className="btn-secondary text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Delete
                  </button>
                  <button
                    onClick={() => setShowIncidentDetailsModal(false)}
                    className="btn-primary"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AttendancePage;
